import { isEqual } from 'date-fns';
import usePrevious from 'hooks/usePrevious';
import { useEffect, useState } from 'react';
import { getTimeWithTimeZone, produce } from 'utility/general';

const useProgressReportData = ({ data }) => {
    const previousData = usePrevious(data?.summaryOfLocalSeriousAdverseEvents);

    useEffect(() => {
        const currentData = data?.summaryOfLocalSeriousAdverseEvents ?? [];

        const check = isEqual(currentData, previousData);

        if (!check) {
            setRows(currentData);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [previousData, data]);

    const defaultAdverseEventsFormList = {
        dateOfSubmission: {
            value: null,
            label: 'Date of Submission to IRB/REC',
            name: 'dateOfSubmission',
            type: 'date',
            validators: ['isRequired'],
            labelItemXs: 4,
        },
        isPatientOfHaInvolved: {
            value: '',
            label: 'Patient of HA involved?',
            name: 'isPatientOfHaInvolved',
            type: 'radio',
            operators: [
                { key: true, label: 'Yes' },
                { key: false, label: 'No' },
            ],
            validators: ['isRequired'],
            labelItemXs: 4,
            returnTrueFalse: true,
        },
        suspectedRelationship: {
            value: '',
            label: 'Suspected Relationship to study',
            name: 'suspectedRelationship',
            type: 'select',
            operators: [
                { key: 0, label: 'Definite' },
                { key: 1, label: 'Probable' },
                { key: 2, label: 'Possible' },
                { key: 3, label: 'Not related' },
                { key: 4, label: 'Not assessable' },
            ],
            validators: ['isRequired'],
            maxLength: 255,
            labelTooltip: 'Please state: Definite / Probable / Possible / Not related / Not assessable',
            labelItemXs: 4,
        },
        isEventLifeThreatening: {
            value: '',
            label: 'Is the event life-threatening',
            name: 'isEventLifeThreatening',
            type: 'radio',
            operators: [
                { key: true, label: 'Yes' },
                { key: false, label: 'No' },
            ],
            validators: ['isRequired'],
            labelItemXs: 4,
            returnTrueFalse: true,
        },
    };

    const columns = [
        {
            field: 'dateOfSubmission',
            headerName: 'Date of Submission to IRB/REC',
            width: 100,
            renderCell: (params) => {
                return params.row.dateOfSubmission
                    ? getTimeWithTimeZone(params.row.dateOfSubmission).format('YYYY-MM-DD')
                    : '-';
            },
        },
        {
            field: 'isPatientOfHaInvolved',
            headerName: 'Patient of HA involved?',
            width: 120,
        },
        {
            field: 'suspectedRelationship',
            headerName: 'Suspected Relationship to study',
            width: 160,
            tooltip: 'Please state: Definite / Probable / Possible / Not related / Not assessable',
            valueGetter: (params) => {
                const value = parseInt(params.value);

                if (value === 0) {
                    return 'Definite';
                } else if (value === 1) {
                    return 'Probable';
                } else if (value === 2) {
                    return 'Possible';
                } else if (value === 3) {
                    return 'Not related';
                } else if (value === 4) {
                    return 'Not assessable';
                }
            },
        },
        {
            field: 'isEventLifeThreatening',
            headerName: 'Is the event life-threatening',
            width: 220,
        },
    ];

    const [rows, setRows] = useState([]);
    const [selectedAdverseEventsRow, setSelectedAdverseEventsRow] = useState(null);
    const [showAdverseEventsDialog, setShowAdverseEventsDialog] = useState(false);
    const [updateSummaryOfLocalSeriousAdverseEvents, setUpdateSummaryOfLocalSeriousAdverseEvents] = useState([]);
    const [deleteSummaryOfLocalSeriousAdverseEvents, setDeleteSummaryOfLocalSeriousAdverseEvents] = useState([]);
    const [newSummaryOfLocalSeriousAdverseEvents, setNewSummaryOfLocalSeriousAdverseEvents] = useState([]);

    // case of edit
    const onAdverseEventsEditSubmit = (row) => {
        // update local data
        const index = rows.findIndex((item) => item.id === row.id);
        let copyRows = [...rows];
        copyRows[index] = row;
        setRows(copyRows);

        const newIndex = newSummaryOfLocalSeriousAdverseEvents.findIndex((item) => {
            return item.id === row.id;
        });

        // if edit record is a new record
        if (newIndex !== -1) {
            setNewSummaryOfLocalSeriousAdverseEvents(
                produce((draft) => {
                    draft[newIndex] = row;
                }),
            );
        } else {
            // if edit record is in remote server

            //update remote data
            const existIndex = updateSummaryOfLocalSeriousAdverseEvents.findIndex((item) => {
                return item.id === row.id;
            });

            setUpdateSummaryOfLocalSeriousAdverseEvents(
                produce((draft) => {
                    if (existIndex !== -1) {
                        draft[existIndex] = row;
                    } else {
                        draft.push(row);
                    }
                }),
            );
        }
    };

    const handleAdverseEventsRecordUpdate = (row) => {
        // update local data
        setRows(
            produce((draft) => {
                draft.push(row);
            }),
        );

        // update remote data
        setNewSummaryOfLocalSeriousAdverseEvents(
            produce((draft) => {
                draft.push(row);
            }),
        );
    };

    const handleAdverseEventsDelete = (row) => {
        // update local data
        setRows(
            produce((draft) => {
                const index = draft.findIndex((item) => {
                    return item.id === row.id;
                });

                if (index !== -1) {
                    draft.splice(index, 1);
                }
            }),
        );

        // update remote data
        const newIndex = newSummaryOfLocalSeriousAdverseEvents.findIndex((item) => {
            return item.id === row.id;
        });

        const updateIndex = updateSummaryOfLocalSeriousAdverseEvents.findIndex((item) => {
            return item.id === row.id;
        });

        if (newIndex !== -1) {
            setNewSummaryOfLocalSeriousAdverseEvents(
                produce((draft) => {
                    draft.splice(newIndex, 1);
                }),
            );
        } else {
            if (updateIndex !== -1) {
                setUpdateSummaryOfLocalSeriousAdverseEvents(
                    produce((draft) => {
                        draft.splice(updateIndex, 1);
                    }),
                );
            }

            setDeleteSummaryOfLocalSeriousAdverseEvents(
                produce((draft) => {
                    draft.push(row.id);
                }),
            );
        }
    };

    const handleAdverseEventsEditClick = (row) => {
        setShowAdverseEventsDialog(true);
        setSelectedAdverseEventsRow(row);
    };

    const resetTableData = () => {
        setNewSummaryOfLocalSeriousAdverseEvents([]);
        setDeleteSummaryOfLocalSeriousAdverseEvents([]);
        setUpdateSummaryOfLocalSeriousAdverseEvents([]);
    };

    return {
        columns,
        rows,
        defaultAdverseEventsFormList,
        selectedAdverseEventsRow,
        showAdverseEventsDialog,
        setSelectedAdverseEventsRow,
        setShowAdverseEventsDialog,
        onAdverseEventsEditSubmit,
        handleAdverseEventsRecordUpdate,
        handleAdverseEventsDelete,
        handleAdverseEventsEditClick,
        newSummaryOfLocalSeriousAdverseEvents,
        updateSummaryOfLocalSeriousAdverseEvents,
        deleteSummaryOfLocalSeriousAdverseEvents,
        resetTableData,
    };
};
export default useProgressReportData;
